import { useMemo, useState } from 'react';
import { useFormikContext } from 'formik';
import { IconButton, InputAdornment } from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import LockIcon from '@mui/icons-material/Lock';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import SearchTwoTone from '@mui/icons-material/SearchTwoTone';
import TextField from '@forms/Components/TextInput';
import { DispatchLegColumns } from '@components/DataGrid/Dispatch/types';
import { getColumnLabel } from '@components/DataGrid/types';
import {
  BoardQueryFormFields,
  findPinnedIndex,
  getColumnDefinition,
  hiddenColumns,
  lockedColumns,
  lockedPinnedFields,
  selectableColumns
} from './types';

import styles from './Page.module.css';

interface ColumnRow {
  column: keyof typeof DispatchLegColumns;
  field: string;
  label: string;
  /** Position within the form's pinnedFields, or -1 when the column is not pinned. */
  pinnedIndex: number;
  /** Index within the form's columns array, or -1 for a locked column the board supplies itself. */
  formIndex: number;
  /** Locked columns lead every board, so they cannot be reordered, unpinned, or removed. */
  locked: boolean;
}

/** A drag that starts and ends in Column Order is a reorder; one that crosses the lists adds or removes a column. */
type DragOrigin = 'order' | 'available';

function reorder<T>(items: T[], fromIndex: number, toIndex: number): T[] {
  const reordered = [...items];
  const [moved] = reordered.splice(fromIndex, 1);
  reordered.splice(toIndex, 0, moved);

  return reordered;
}

function insert<T>(items: T[], index: number, item: T): T[] {
  const inserted = [...items];
  inserted.splice(index, 0, item);

  return inserted;
}

/**
 * The query's columns alongside every dispatch column it is not using, each list a drop target for the other.
 * Within Column Order, pinned columns lead the list because the grid hoists them to the left of the board, and
 * each group reorders within itself — pinned position lives in pinnedFields, everything else in columns.
 */
export default function ColumnSelection() {
  const { values, setFieldValue } = useFormikContext<BoardQueryFormFields>();
  const [drag, setDrag] = useState<{ field: string; origin: DragOrigin } | null>(null);
  const [dropField, setDropField] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');

  const rows = useMemo(() => {
    const toRow = (column: keyof typeof DispatchLegColumns, formIndex: number, locked: boolean): ColumnRow | undefined => {
      const definition = getColumnDefinition(column);
      if (!definition) return undefined;

      return {
        column,
        formIndex,
        locked,
        field: definition.field,
        label: getColumnLabel(definition),
        pinnedIndex: findPinnedIndex(values.pinnedFields, definition)
      };
    };

    const lockedRows = lockedColumns.reduce<ColumnRow[]>((accumulator, column) => {
      const row = toRow(column, values.columns.indexOf(column), true);
      if (row) accumulator.push(row);

      return accumulator;
    }, []);

    const queryRows = values.columns.reduce<ColumnRow[]>((accumulator, column, formIndex) => {
      if (lockedColumns.includes(column) || hiddenColumns.includes(column)) return accumulator;

      const row = toRow(column, formIndex, false);
      if (row) accumulator.push(row);

      return accumulator;
    }, []);

    // Sort is stable, so unpinned columns keep their columns-array order
    queryRows.sort((left, right) => {
      if (left.pinnedIndex < 0 && right.pinnedIndex < 0) return 0;
      if (left.pinnedIndex < 0) return 1;
      if (right.pinnedIndex < 0) return -1;
      return left.pinnedIndex - right.pinnedIndex;
    });

    return [...lockedRows, ...queryRows];
  }, [values.columns, values.pinnedFields]);

  const available = useMemo(() => {
    const search = searchText.trim().toLowerCase();

    return selectableColumns.filter(
      (candidate) => !values.columns.includes(candidate.column) && candidate.label.toLowerCase().includes(search)
    );
  }, [values.columns, searchText]);

  const moveRow = (source: ColumnRow | undefined, target: ColumnRow | undefined) => {
    if (!source || !target || source.locked || target.locked || source.field === target.field) return;

    const isSourcePinned = source.pinnedIndex >= 0;
    // The two groups are held in separate arrays, so moving across them would be a pin change rather than a reorder
    if (isSourcePinned !== target.pinnedIndex >= 0) return;

    if (isSourcePinned) {
      void setFieldValue('pinnedFields', reorder(values.pinnedFields, source.pinnedIndex, target.pinnedIndex), true);
    } else {
      void setFieldValue('columns', reorder(values.columns, source.formIndex, target.formIndex), true);
    }
  };

  /** Takes the dropped-on row's place: onto the pinned group the column arrives pinned, otherwise it arrives unpinned. */
  const addColumn = (column: keyof typeof DispatchLegColumns, target: ColumnRow | undefined) => {
    const definition = getColumnDefinition(column);
    if (!definition) return;

    // Order Number is not in the columns array, so a drop onto it falls to the front
    void setFieldValue('columns', insert(values.columns, target ? Math.max(target.formIndex, 0) : values.columns.length, column), true);

    if (!target || target.pinnedIndex < 0) return;

    // A drop onto a locked row lands just after the locked pins rather than displacing them
    void setFieldValue(
      'pinnedFields',
      insert(values.pinnedFields, target.locked ? lockedPinnedFields.length : target.pinnedIndex, definition.field),
      true
    );
  };

  const removeColumn = (row: ColumnRow) => {
    if (row.locked) return;

    void setFieldValue(
      'columns',
      values.columns.filter((column) => column !== row.column),
      true
    );

    if (row.pinnedIndex >= 0) {
      void setFieldValue(
        'pinnedFields',
        values.pinnedFields.filter((_, index) => index !== row.pinnedIndex),
        true
      );
    }
  };

  const togglePin = (row: ColumnRow) => {
    if (row.locked) return;

    const pinnedFields =
      row.pinnedIndex >= 0 ? values.pinnedFields.filter((_, index) => index !== row.pinnedIndex) : [...values.pinnedFields, row.field];

    void setFieldValue('pinnedFields', pinnedFields, true);
  };

  const endDrag = () => {
    setDrag(null);
    setDropField(null);
  };

  /** Undefined target means the drop landed on the list itself rather than a row, which appends the column. */
  const handleDropOnOrder = (target: ColumnRow | undefined) => {
    endDrag();
    if (drag?.origin !== 'available') return;

    const candidate = selectableColumns.find((selectable) => selectable.field === drag.field);
    if (candidate) addColumn(candidate.column, target);
  };

  const handleDropOnAvailable = () => {
    endDrag();
    if (drag?.origin !== 'order') return;

    const row = rows.find((candidate) => candidate.field === drag.field);
    if (row) removeColumn(row);
  };

  return (
    <>
      <div className={styles['column-order']}>
        <span className={styles['section-title']}>Column Order ({rows.length})</span>

        <ul
          className={styles['column-list']}
          onDragOver={(event) => {
            // Without this the drop is rejected and nothing is ever added
            event.preventDefault();
          }}
          onDrop={() => {
            handleDropOnOrder(undefined);
          }}
        >
          {rows.map((row, index) => (
            <li
              key={`column-${row.column}`}
              draggable={!row.locked}
              tabIndex={0}
              aria-label={`${row.label}${row.locked ? ', always on the board' : row.pinnedIndex >= 0 ? ', pinned to the left of the board' : ''}`}
              onKeyDown={(event) => {
                if (row.locked) return;

                if (event.key === 'Delete' || event.key === 'Backspace') {
                  event.preventDefault();
                  removeColumn(row);
                  return;
                }

                if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;

                event.preventDefault();
                moveRow(row, rows[index + (event.key === 'ArrowUp' ? -1 : 1)]);
              }}
              onDragStart={() => {
                setDrag({ field: row.field, origin: 'order' });
              }}
              onDragEnd={endDrag}
              onDragOver={(event) => {
                event.preventDefault();
              }}
              onDragEnter={() => {
                if (drag?.origin === 'available') {
                  setDropField(row.field);
                  return;
                }

                moveRow(
                  rows.find((candidate) => candidate.field === drag?.field),
                  row
                );
              }}
              onDrop={(event) => {
                // Keeps the list's own handler from treating this as a drop onto empty space
                event.stopPropagation();
                handleDropOnOrder(row);
              }}
              className={`${styles['column-item']} ${row.locked ? styles.locked : ''} ${row.pinnedIndex >= 0 ? styles.pinned : ''} ${
                drag?.field === row.field ? styles.dragging : ''
              } ${dropField === row.field ? styles['drop-target'] : ''}`}
            >
              {row.locked ? (
                <LockIcon
                  fontSize='small'
                  className={styles['lock-icon']}
                  titleAccess='Always on the board and cannot be moved'
                />
              ) : (
                <IconButton
                  size='small'
                  className={styles['pin-btn']}
                  title={row.pinnedIndex >= 0 ? 'Unpin from the left of the board' : 'Pin to the left of the board'}
                  onClick={() => {
                    togglePin(row);
                  }}
                >
                  {row.pinnedIndex >= 0 ? <PushPinIcon fontSize='small' /> : <PushPinOutlinedIcon fontSize='small' />}
                </IconButton>
              )}

              <span className={styles['column-label']}>{row.label}</span>

              {!row.locked && (
                <DragIndicatorIcon
                  fontSize='small'
                  className={styles['drag-icon']}
                />
              )}
            </li>
          ))}
        </ul>
      </div>

      <div className={styles['available-columns']}>
        {/* Counts the filtered list rather than every unused column so the number never contradicts what is shown */}
        <span className={styles['section-title']}>Available Columns ({available.length})</span>

        <TextField
          placeholder='Filter Columns'
          className={styles['column-search']}
          value={searchText}
          onChange={(event) => {
            setSearchText(event.target.value);
          }}
          customProps={{
            labelText: '',
            isDirty: false,
            onClear: () => {
              setSearchText('');
            },
            startAdornment: (
              <InputAdornment position='start'>
                <SearchTwoTone />
              </InputAdornment>
            )
          }}
        />

        <ul
          className={`${styles['column-list']} ${styles['available-list']}`}
          onDragOver={(event) => {
            event.preventDefault();
          }}
          onDrop={handleDropOnAvailable}
        >
          {available.length === 0 ? (
            <li className={styles['column-empty']}>{searchText.trim() ? 'No Matching Columns' : 'Every Column Is In Use'}</li>
          ) : (
            available.map((candidate) => (
              <li
                key={`available-${candidate.column}`}
                draggable
                tabIndex={0}
                aria-label={candidate.label}
                onKeyDown={(event) => {
                  if (event.key !== 'Enter' && event.key !== ' ') return;

                  event.preventDefault();
                  addColumn(candidate.column, undefined);
                }}
                onDragStart={() => {
                  setDrag({ field: candidate.field, origin: 'available' });
                }}
                onDragEnd={endDrag}
                className={`${styles['column-item']} ${drag?.field === candidate.field ? styles.dragging : ''}`}
              >
                <span className={styles['column-label']}>{candidate.label}</span>

                <DragIndicatorIcon
                  fontSize='small'
                  className={styles['drag-icon']}
                />
              </li>
            ))
          )}
        </ul>
      </div>
    </>
  );
}
